import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Navigate, Outlet } from "react-router-dom";
import { toast } from "sonner";
import {
    SquaresFour,
    Receipt,
    ForkKnife,
    EnvelopeSimple,
    ChartBar,
    UsersThree,
    SignOut,
    BellRinging,
    ArrowLeft,
    Gear,
    Storefront,
    Calculator,
    Fire,
    Printer,
    ChartLineUp,
    Motorcycle,
    AddressBook,
    IdentificationBadge,
    Fingerprint,
} from "@phosphor-icons/react";
import Logo from "@/components/site/Logo";
import NewOrderAlert from "@/admin/components/NewOrderAlert";
import { useAdmin } from "@/context/AdminContext";
import { api, authHeaders } from "@/lib/api";

const NAV = [
    { to: "/admin", label: "Overview", icon: SquaresFour, end: true, module: "overview" },
    { to: "/admin/kitchen", label: "Cocina", icon: Fire, module: "kitchen" },
    { to: "/admin/orders", label: "Orders", icon: Receipt, module: "orders" },
    { to: "/admin/pos", label: "POS", icon: Storefront, module: "pos" },
    { to: "/admin/delivery", label: "Delivery", icon: Motorcycle, module: "delivery" },
    { to: "/admin/menu", label: "Menu", icon: ForkKnife, ownerOnly: true, module: "menu" },
    { to: "/admin/accounting", label: "Contabilidad", icon: Calculator, ownerOnly: true, module: "accounting" },
    { to: "/admin/analytics", label: "Analítica", icon: ChartLineUp, ownerOnly: true, module: "analytics" },
    { to: "/admin/subscribers", label: "Subscribers", icon: EnvelopeSimple, module: "subscribers" },
    { to: "/admin/customers", label: "Clientes", icon: AddressBook, module: "customers" },
    { to: "/admin/staff", label: "Personal", icon: IdentificationBadge, module: "employees" },
    { to: "/admin/attendance", label: "Asistencia", icon: Fingerprint, module: "attendance" },
    { to: "/admin/reports", label: "Reports", icon: ChartBar, module: "reports" },
    { to: "/admin/sales-report", label: "Reporte Ventas", icon: Calculator, module: "reports" },
    { to: "/admin/users", label: "Users", icon: UsersThree, ownerOnly: true, module: "users" },
    { to: "/admin/printer-settings", label: "Impresora", icon: Printer, module: "printer" },
    { to: "/admin/settings", label: "Settings", icon: Gear, ownerOnly: true, module: "settings" },
];

/**
 * Plays a fulfillment-specific alert.
 *  - "delivery": 4 lower urgent beeps (time-sensitive)
 *  - "pickup"  : 3 ascending happy beeps (casual)
 */
function playAlertSequence(fulfillment = "pickup") {
    try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        const now = ctx.currentTime;

        const beeps =
            fulfillment === "delivery"
                ? [
                      { freq: 620, start: 0.0, dur: 0.16, vol: 0.36 },
                      { freq: 520, start: 0.18, dur: 0.16, vol: 0.36 },
                      { freq: 620, start: 0.36, dur: 0.16, vol: 0.36 },
                      { freq: 520, start: 0.54, dur: 0.36, vol: 0.4 },
                  ]
                : [
                      { freq: 880, start: 0.0, dur: 0.18, vol: 0.32 },
                      { freq: 988, start: 0.22, dur: 0.18, vol: 0.32 },
                      { freq: 1175, start: 0.44, dur: 0.32, vol: 0.36 },
                  ];

        beeps.forEach((b) => {
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.connect(gain);
            gain.connect(ctx.destination);
            osc.type = fulfillment === "delivery" ? "square" : "sine";
            osc.frequency.setValueAtTime(b.freq, now + b.start);
            gain.gain.setValueAtTime(0.0001, now + b.start);
            gain.gain.exponentialRampToValueAtTime(b.vol, now + b.start + 0.02);
            gain.gain.exponentialRampToValueAtTime(
                0.0001,
                now + b.start + b.dur,
            );
            osc.start(now + b.start);
            osc.stop(now + b.start + b.dur + 0.05);
        });
    } catch {
        // audio failed silently
    }
}

export default function AdminLayout() {
    const { token, user, logout, isOwner, hasModule } = useAdmin();
    const seenIds = useRef(new Set());
    const initialized = useRef(false);
    const [soundOn, setSoundOn] = useState(() => localStorage.getItem("jw_admin_sound") !== "off");
    const [pendingAlerts, setPendingAlerts] = useState([]);

    // Ask for native notification permission once after admin signs in.
    useEffect(() => {
        if (!token) return;
        if (typeof Notification === "undefined") return;
        if (Notification.permission === "default") {
            Notification.requestPermission().catch(() => {});
        }
    }, [token]);

    // Live new-order detector
    useEffect(() => {
        if (!token) return;
        let alive = true;
        const tick = async () => {
            try {
                const r = await api.get("/admin/orders/recent-paid", { headers: authHeaders() });
                if (!alive) return;
                const orders = r.data.orders || [];
                if (!initialized.current) {
                    orders.forEach((o) => seenIds.current.add(o.id));
                    initialized.current = true;
                    return;
                }
                // POS sales are rung up in-store — no incoming-order alert for them.
                const fresh = orders.filter((o) => !seenIds.current.has(o.id));
                fresh.filter((o) => o.channel === "pos").forEach((o) => seenIds.current.add(o.id));
                const incoming = fresh.filter((o) => o.channel !== "pos");
                if (incoming.length === 0) return;

                // Fetch full details for the new orders so the alert shows items
                let detailed = incoming;
                try {
                    const all = await api.get("/orders", { headers: authHeaders() });
                    const map = new Map((all.data || []).map((o) => [o.id, o]));
                    detailed = incoming.map((o) => map.get(o.id) || o);
                } catch {
                    // fallback to the lite list
                }

                detailed.forEach((o) => {
                    seenIds.current.add(o.id);
                    // browser-level notification (works even if tab is in background)
                    if (
                        typeof Notification !== "undefined" &&
                        Notification.permission === "granted"
                    ) {
                        try {
                            new Notification("Nueva orden — J & W Deli", {
                                body: `${o.customer_name || "Cliente"} · $${Number(o.total).toFixed(2)}`,
                                tag: `order-${o.id}`,
                                requireInteraction: true,
                            });
                        } catch {
                            /* noop */
                        }
                    }
                    toast.success(
                        `Nueva orden · ${o.customer_name || "Cliente"} · $${Number(o.total).toFixed(2)}`,
                        { duration: 6000 },
                    );
                });
                setPendingAlerts((prev) => [...prev, ...detailed]);
            } catch {
                // ignore
            }
        };
        tick();
        const id = setInterval(tick, 6000);
        return () => {
            alive = false;
            clearInterval(id);
        };
    }, [token]);

    // Repeat the alert sound every 4s while there are unacknowledged orders.
    // Uses the fulfillment type of the front-most order in the queue.
    useEffect(() => {
        if (!soundOn || pendingAlerts.length === 0) return;
        const fulfillment = pendingAlerts[0]?.fulfillment || "pickup";
        playAlertSequence(fulfillment);
        const id = setInterval(() => playAlertSequence(fulfillment), 4000);
        return () => clearInterval(id);
    }, [pendingAlerts, soundOn]);

    const dismissAlert = (id) =>
        setPendingAlerts((prev) => prev.filter((o) => o.id !== id));
    const dismissAllAlerts = () => setPendingAlerts([]);

    const toggleSound = () => {
        setSoundOn((s) => {
            const next = !s;
            localStorage.setItem("jw_admin_sound", next ? "on" : "off");
            toast(next ? "Sound alerts ON" : "Sound alerts OFF");
            if (next) playAlertSequence("pickup");
            return next;
        });
    };

    if (!token || !user) return <Navigate to="/admin/login" replace />;

    const visibleNav = NAV.filter((n) => {
        if (n.ownerOnly && !isOwner) return false;
        // Owner sees everything; staff is gated by per-module access.
        if (isOwner) return true;
        return n.module ? hasModule(n.module) : true;
    });

    return (
        <div className="min-h-screen bg-muted flex">
            {/* Sidebar */}
            <aside
                data-testid="admin-sidebar"
                className="hidden md:flex w-64 shrink-0 flex-col bg-accent text-accent-foreground sticky top-0 h-screen"
            >
                <div className="px-6 py-6 border-b border-white/10">
                    <Link to="/" className="flex items-center gap-3" data-testid="admin-back-home">
                        <Logo size={44} className="rounded-full bg-white p-0.5 shadow-md" />
                        <div>
                            <p className="font-display font-black tracking-tight leading-none">J & W Admin</p>
                            <p className="text-[10px] uppercase tracking-widest text-white/50 mt-1">
                                {user.role}
                            </p>
                        </div>
                    </Link>
                </div>

                <nav className="flex-1 p-4 space-y-1">
                    {visibleNav.map(({ to, label, icon: Icon, end }) => (
                        <NavLink
                            key={to}
                            to={to}
                            end={end}
                            data-testid={`nav-${label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-bold tracking-wide transition ${
                                    isActive
                                        ? "bg-primary text-primary-foreground"
                                        : "text-white/70 hover:bg-white/5 hover:text-white"
                                }`
                            }
                        >
                            <Icon size={18} weight="bold" />
                            {label}
                        </NavLink>
                    ))}
                </nav>

                <div className="p-4 border-t border-white/10 space-y-2">
                    <button
                        data-testid="toggle-sound"
                        onClick={toggleSound}
                        className="w-full flex items-center justify-between px-3 py-2 rounded-xl text-xs font-bold uppercase tracking-widest text-white/70 hover:bg-white/5"
                    >
                        <span className="inline-flex items-center gap-2">
                            <BellRinging size={14} weight="bold" />
                            Sound
                        </span>
                        <span className={soundOn ? "text-secondary" : "text-white/40"}>
                            {soundOn ? "ON" : "OFF"}
                        </span>
                    </button>
                    <div className="px-3 py-2 rounded-xl bg-white/5">
                        <p className="text-xs uppercase tracking-widest text-white/40">Signed in</p>
                        <p className="text-sm font-bold truncate">{user.email}</p>
                    </div>
                    <button
                        data-testid="admin-signout"
                        onClick={logout}
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-bold uppercase tracking-widest text-white/70 hover:bg-white/5"
                    >
                        <SignOut size={14} weight="bold" />
                        Sign out
                    </button>
                </div>
            </aside>

            {/* Mobile bar */}
            <div className="md:hidden fixed top-0 inset-x-0 z-30 bg-accent text-accent-foreground shadow-md">
                <div className="flex items-center gap-2 px-3 h-12 justify-between">
                    <Link to="/" className="flex items-center gap-2 min-w-0" data-testid="admin-mobile-home">
                        <ArrowLeft size={16} weight="bold" className="shrink-0" />
                        <Logo size={24} className="rounded-full bg-white shrink-0" />
                        <span className="font-display font-black text-sm truncate">J&amp;W Admin</span>
                    </Link>
                    <div className="flex items-center gap-2 shrink-0">
                        <button
                            data-testid="mobile-sound"
                            onClick={toggleSound}
                            className="h-8 w-8 grid place-items-center rounded-full bg-white/10"
                            title="Sonido"
                        >
                            <BellRinging size={14} weight="bold" className={soundOn ? "text-secondary" : "opacity-40"} />
                        </button>
                        <button
                            onClick={logout}
                            data-testid="admin-mobile-signout"
                            className="h-8 w-8 grid place-items-center rounded-full bg-white/10"
                            title="Salir"
                        >
                            <SignOut size={14} weight="bold" />
                        </button>
                    </div>
                </div>
                <nav className="flex gap-1 px-2 pb-1.5 overflow-x-auto scrollbar-hide">
                    {visibleNav.map(({ to, label, icon: Icon, end }) => (
                        <NavLink
                            key={to}
                            to={to}
                            end={end}
                            data-testid={`mnav-${label.toLowerCase()}`}
                            className={({ isActive }) =>
                                `shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold whitespace-nowrap ${
                                    isActive ? "bg-primary text-primary-foreground" : "bg-white/10"
                                }`
                            }
                        >
                            <Icon size={14} weight="bold" />
                            {label}
                        </NavLink>
                    ))}
                </nav>
            </div>

            <main className="flex-1 min-w-0 px-3 sm:px-4 md:px-8 pt-24 md:pt-8 pb-16">
                <Outlet />
            </main>

            <NewOrderAlert
                orders={pendingAlerts}
                onDismiss={dismissAlert}
                onDismissAll={dismissAllAlerts}
            />
        </div>
    );
}
